import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';

import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import moment from 'moment';

am4core.useTheme(am4themes_animated);

function FuelConsumptionRateBarLineChart({ data: docs, idleFuelRate, selectedGroup, assetData, standardFuelRate, loading, ...props }) {

    const [data, setData] = useState([]);
    const [header, setHeader] = useState('--');
    const [standardRateHour, setStandardRateHour] = useState(0);
    const [standardRateKm, setStandardRateKm] = useState(0);

    useEffect(() => {
        setData(docs)
    }, [docs]);

    useLayoutEffect(() => {
        if (selectedGroup && assetData) {
            const title = selectedGroup === 'w' ? 'Weekly' : selectedGroup === 'm' ? 'Monthwise' : selectedGroup === 'y' ? 'Yearwise' : 'Daywise';
            setHeader(`${title} Consumption rate (mileage) for ${assetData.assetCode}`)
        }
    }, [selectedGroup, assetData]);

    useEffect(() => {
        if (standardFuelRate && standardFuelRate.length) {
            const hrRate = standardFuelRate.filter(s => (s.perType && s.perType.name && s.perType.name === 'HOURS'))
            if (hrRate && hrRate[0] && hrRate[0]['fuelQty'])
                setStandardRateHour(hrRate[0]['fuelQty'])
            const kmRate = standardFuelRate.filter(s => (s.perType && s.perType.name && s.perType.name === 'KMS'))
            if (kmRate && kmRate[0] && kmRate[0]['fuelQty'])
                setStandardRateKm(kmRate[0]['fuelQty'])
        }
    }, [standardFuelRate]);

    const chart = useRef(null);


    useLayoutEffect(() => {
        if (!loading && data && data.length) {

            let totalConsumptionRate = 0;
            data.forEach(d => {
                totalConsumptionRate += Number(d.consumption_rate)
            })

            let x = am4core.create("fuelConsumeRate", am4charts.XYChart);
            // am4core.addLicense("ch-custom-attribution");

            let title = x.titles.create();
            title.text = header || '';
            title.fontSize = 15;
            title.marginBottom = 18;

            // Export
            x.exporting.menu = new am4core.ExportMenu();
            x.exporting.filePrefix = "fuel_consumption_rate_time" + new Date().getTime();;

            x.paddingRight = 20;

            let dateAxis = x.xAxes.push(new am4charts.CategoryAxis());
            dateAxis.dataFields.category = "date";
            dateAxis.title.text = selectedGroup === 'm' ? 'Month' : selectedGroup === 'w' ? 'Week' : selectedGroup === 'y' ? 'Year' : 'Date Range';
            // Format the labels
            // dateAxis.renderer.labels.template.adapter.add("text", function (label, target, key) {
            //     console.log(label, target, key);
            //     return label;
            //     // return moment(label).format('MM-DD');
            // });
            // dateAxis.title.text = "Date(DD-MM)"
            // categoryAxis.renderer.minGridDistance = 30;
            // dateAxis.renderer.labels.template.rotation = -90;

            let valueAxis = x.yAxes.push(new am4charts.ValueAxis());
            valueAxis.title.text = 'Consumption Rate (Km/ltr)'

            let columnSeries = x.series.push(new am4charts.ColumnSeries());
            columnSeries.name = "Consumption Rate";
            columnSeries.dataFields.valueY = "consumption_rate";
            columnSeries.dataFields.categoryX = "date";
            // columnSeries.title.text = 'Fuel Consumed'

            columnSeries.columns.template.tooltipText = "[#fff font-size: 12px]{name} in {categoryX}:\n[/][#fff font-size: 15px]{valueY}[/] [#fff]{additional}[/]"
            columnSeries.columns.template.propertyFields.fillOpacity = "fillOpacity";
            columnSeries.columns.template.propertyFields.stroke = "stroke";
            // columnSeries.columns.template.width = 20;
            // columnSeries.columns.template.strokeOpacity = 0;
            columnSeries.columns.template.propertyFields.strokeWidth = "strokeWidth";
            columnSeries.columns.template.propertyFields.strokeDasharray = "columnDash";
            columnSeries.tooltip.label.textAlign = "middle";
            columnSeries.columns.template.strokeOpacity = 0;
            // columnSeries.columns.template.stroke = am4core.color("#000000")

            // Apply random color from predefined color set
            columnSeries.columns.template.adapter.add("fill", function (fill, target) {
                return x.colors.getIndex(target.dataItem.index);
            });


            function adjustColumnWidth() {
                let totalColumns = data.length;
                let totalWidth = dateAxis.pixelWidth;

                // Adjust the width here. This is an example formula.
                // Adjust the formula as per your requirement.
                let columnWidth = Math.min(20, totalWidth / (totalColumns * 2));

                columnSeries.columns.template.width = am4core.percent(columnWidth);
                columnSeries.columns.template.column.cornerRadiusTopLeft = am4core.percent(columnWidth * 2);
                columnSeries.columns.template.column.cornerRadiusTopRight = am4core.percent(columnWidth * 2);

            }
            columnSeries.hidden = true;
            // Adjust column width initially and on data change
            adjustColumnWidth();
            x.events.on("datavalidated", adjustColumnWidth);

            if (standardRateKm) {
                let averageGuide = valueAxis.axisRanges.create();
                averageGuide.value = (1 / standardRateKm).toFixed(2);
                averageGuide.grid.stroke = am4core.color("#000");
                averageGuide.grid.strokeWidth = 1.5;
                averageGuide.grid.strokeOpacity = 0.7;
                averageGuide.grid.strokeDasharray = "4,4";
                // averageGuide.label.text = `${(1 / standardRateKm).toFixed(2)}`;
                // averageGuide.label.align = "right";
                // averageGuide.label.verticalCenter = "bottom";
            }


            let lineSeries = x.series.push(new am4charts.LineSeries());
            lineSeries.name = "Consumption Rate";
            lineSeries.dataFields.valueY = "consumption_rate";
            lineSeries.dataFields.categoryX = "date";

            lineSeries.stroke = am4core.color("#fdd400");
            lineSeries.strokeWidth = 2;
            lineSeries.propertyFields.strokeDasharray = "lineDash";
            lineSeries.tooltip.label.textAlign = "middle";

            lineSeries.legendSettings.labelText = `Consumption Rate \n(Standard Rate ${standardRateKm ? (1 / standardRateKm).toFixed(2) + ' Km/ltr' : 'NA'}, Avg Rate: ${(totalConsumptionRate / data.length).toFixed(2)} Km/ltr)`;

            let bullet = lineSeries.bullets.push(new am4charts.Bullet());
            bullet.fill = am4core.color("#fdd400"); // tooltips grab fill from parent by default
            bullet.tooltipText = "[#333 font-size: 11px]{name} in {categoryX}:\n[/][#333 font-size: 15px]{valueY} Km/ltr[/] [#333]{additional}[/]"

            let circle = bullet.createChild(am4core.Circle);
            circle.radius = 2;
            circle.fill = am4core.color("#fff");
            circle.strokeWidth = 1;

            // let label = x.plotContainer.createChild(am4core.Label);
            // label.text = `Standard Rate ${(1 / standardRateKm).toFixed(2)}`
            // label.fontSize = 12;
            // label.align = "center";
            // label.valign = "bottom";
            // label.dy = 10; // Adjust this value as needed

            x.legend = new am4charts.Legend();

            // Add cursor
            x.cursor = new am4charts.XYCursor();
            x.cursor.fullWidthLineX = true;
            x.cursor.xAxis = dateAxis;
            x.cursor.lineX.strokeOpacity = 0;
            x.cursor.lineX.fill = am4core.color("#000");
            x.cursor.lineX.fillOpacity = 0.1;

            // Add scrollbar
            if (data && data.length > 25) {
                let scrollbarX = new am4charts.XYChartScrollbar();
                scrollbarX.series.push(columnSeries);
                // scrollbarX.marginBottom = 50;
                x.scrollbarX = scrollbarX;
            }

            x.cursor = new am4charts.XYCursor();

            x.data = data;

            chart.current = x;

            return () => {
                if (x)
                    x.dispose();
            };
        }
    }, [data, header]);

    useLayoutEffect(() => {
        if (chart && chart.current)
            chart.current.paddingRight = props.paddingRight;
    }, [props.paddingRight]);

    return <>{loading ? <div style={{ width: "100%", height: "500px", display: 'flex', alignItems: 'center', justifyContent: 'center' }} >Loading ...</div> : <div id="fuelConsumeRate" style={{ width: "100%", height: "500px" }} />}</>
}

export default FuelConsumptionRateBarLineChart;